import { useState } from 'react';
import { apiClient } from '@services/api';
import { useTranslation } from '@stores/translationStore';
import { emitActivityChanged } from '@utils/activityEvents';
import './PhotoCartCard.scss';

/** A cart the hotel built from the guest's product photos (message metadata type "photo_cart"). */
export interface PhotoCartMetadata {
  type: 'photo_cart';
  cartId: string;
  status: 'pending' | 'placed' | 'cancelled' | 'failed';
  stationName: string;
  currencySymbol: string;
  total: number;
  items: { name: string; quantity: number; unitPrice: number; lineTotal: number }[];
  leftOut?: string[];
  expiresAt?: string;
  orderNumber?: string | null;
}

interface Props {
  metadata: PhotoCartMetadata;
  onDecided?: (next: PhotoCartMetadata) => void;
}

/**
 * The cart from a chat photo: what was recognised, priced, with Confirm (charge to the room) and Cancel.
 * Mirrors the WhatsApp buttons — one answer per cart; the server refuses a second.
 */
export default function PhotoCartCard({ metadata, onDecided }: Props) {
  const { t } = useTranslation();
  const tr = (key: string, fallback: string) => (t(key) !== key ? t(key) : fallback);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const expired = metadata.status === 'pending' && !!metadata.expiresAt && new Date(metadata.expiresAt) < new Date();
  const money = (n: number) => `${metadata.currencySymbol}${Number.isInteger(n) ? n : n.toFixed(2)}`;

  const decide = async (decision: 'confirm' | 'cancel') => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      const { data } = await apiClient.post<{ status: string; message: string; orderNumber?: string }>(
        `/api/v1/guest/messages/photo-cart/${metadata.cartId}/${decision}`);
      setNote(data.message);
      const status: PhotoCartMetadata['status'] =
        data.status === 'placed' ? 'placed' : data.status === 'cancelled' ? 'cancelled' : 'failed';
      onDecided?.({ ...metadata, status, orderNumber: data.orderNumber ?? null });
      if (status === 'placed') emitActivityChanged();
    } catch {
      setNote(tr('photo_cart.error', 'Something went wrong — nothing was charged. Please try again.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`photo-cart-card photo-cart-card--${expired ? 'expired' : metadata.status}`}>
      <div className="photo-cart-card__header">
        <span>🛒 {metadata.stationName}</span>
        {metadata.status === 'placed' && (
          <span className="photo-cart-card__badge">
            {tr('photo_cart.placed', 'Charged')}{metadata.orderNumber ? ` · #${metadata.orderNumber}` : ''}
          </span>
        )}
        {metadata.status === 'cancelled' && <span className="photo-cart-card__badge">{tr('photo_cart.cancelled', 'Cancelled')}</span>}
      </div>
      <ul className="photo-cart-card__items">
        {metadata.items.map((item, i) => (
          <li key={i}>
            <span>{item.quantity}× {item.name}</span>
            <span>{item.unitPrice > 0 ? money(item.lineTotal) : tr('photo_cart.free', 'Complimentary')}</span>
          </li>
        ))}
      </ul>
      <div className="photo-cart-card__total">
        <span>{tr('photo_cart.total', 'Total')}</span>
        <span>{metadata.total > 0 ? money(metadata.total) : tr('photo_cart.free', 'Complimentary')}</span>
      </div>
      {metadata.leftOut && metadata.leftOut.length > 0 && (
        <div className="photo-cart-card__left-out">
          <span>{tr('photo_cart.left_out', 'Not included (not sure):')}</span>
          {metadata.leftOut.map((x, i) => <div key={i}>• {x}</div>)}
        </div>
      )}
      {metadata.status === 'pending' && !expired && (
        <div className="photo-cart-card__actions">
          <button type="button" className="photo-cart-card__confirm" disabled={busy} onClick={() => decide('confirm')}>
            {metadata.total > 0 ? tr('photo_cart.confirm_charge', 'Confirm — charge to room') : tr('photo_cart.confirm', 'Confirm')}
          </button>
          <button type="button" className="photo-cart-card__cancel" disabled={busy} onClick={() => decide('cancel')}>
            {tr('photo_cart.cancel', 'Cancel')}
          </button>
        </div>
      )}
      {expired && <div className="photo-cart-card__note">{tr('photo_cart.expired', 'This cart has expired — send the photos again.')}</div>}
      {note && <div className="photo-cart-card__note">{note}</div>}
    </div>
  );
}
